// src/hooks/dashboard/prefetch/dashboardPrefetch.ts

import type { QueryClient } from "@tanstack/react-query";
import type { DashboardType } from "@/types/dashboards/dashboardTypes";
import { MetricKeys } from "@/constants/metrics";
import { chartQueryKeys } from "@/constants/queryKeys";
import { fetchHistoryBatched } from "@/services/queries/charts/chartQueries";
import {
  categoryCurrentQueryOptions,
  categoryCurrentRatiosQueryOptions,
  categoryCurrentChangesQueryOptions,
  categoryCurrentRollupsQueryOptions,
  categoryPerformanceQueryOptions,
} from "@/services/queries/dashboards/categoriesQueries";
import {
  buildCurrentQueryFn,
  buildPercentageChangesQueryFn,
  buildRollupsQueryFn,
} from "../queryBuilders/dashboardQueryBuilders";

const STALE_TIME = 1000 * 60 * 5; // 5 minutes

/**
 * Prefetches category-level data used by overview components
 */
export function prefetchCategoryData(
  queryClient: QueryClient,
  categoryId: number
): Array<Promise<unknown>> {
  return [
    // Category snapshots
    queryClient.prefetchQuery(categoryCurrentQueryOptions(categoryId)),
    queryClient.prefetchQuery(categoryCurrentRatiosQueryOptions(categoryId)),
    queryClient.prefetchQuery(categoryCurrentChangesQueryOptions(categoryId)),
    queryClient.prefetchQuery(categoryCurrentRollupsQueryOptions(categoryId)),

    // Category performance vs BTC/ETH
    queryClient.prefetchQuery(categoryPerformanceQueryOptions(categoryId)),

    // Price history for sparklines
    queryClient.prefetchQuery({
      queryKey: ["history", "metrics", { categoryId, keys: [MetricKeys.PriceClose] }] as const,
      queryFn: ({ signal }) => fetchHistoryBatched("metrics", { 
        categoryId, 
        keys: [MetricKeys.PriceClose], 
        signal 
      }),
    }),
  ];
}

/**
 * Prefetches dashboard table data
 */
export function prefetchDashboardData(
  queryClient: QueryClient,
  dashboardType: DashboardType,
  categoryId: number
): Array<Promise<unknown>> {
  return [
    // Current data
    queryClient.prefetchQuery({
      queryKey: ["dashboard", "data", { dashboardType, dataType: "current", categoryId }] as const,
      queryFn: ({ signal }) => buildCurrentQueryFn(categoryId, signal),
      staleTime: STALE_TIME,
    }),

    // Percentage changes
    queryClient.prefetchQuery({
      queryKey: ["dashboard", "data", { dashboardType, dataType: "percentageChanges", categoryId }] as const,
      queryFn: ({ signal }) => buildPercentageChangesQueryFn(categoryId, signal),
      staleTime: STALE_TIME,
    }),

    // Rollups
    queryClient.prefetchQuery({
      queryKey: ["dashboard", "data", { dashboardType, dataType: "rollups", categoryId }] as const,
      queryFn: ({ signal }) => buildRollupsQueryFn(categoryId, signal),
      staleTime: STALE_TIME,
    }),
  ];
}

/**
 * Prefetches chart data (lower priority, loaded after critical data)
 * Prefetches the first 4 metrics and first 4 ratios for the dashboard
 */
export async function prefetchChartsData(
  queryClient: QueryClient,
  categoryId: number
): Promise<void> {
  // Metrics charts (first 4: volume, market cap, fdv, dex trading volume)
  const metricKeys = [
    MetricKeys.Volume,
    MetricKeys.MarketCap,
    MetricKeys.FDV,
    MetricKeys.DexVolume,
  ];

  await queryClient.prefetchQuery({
    queryKey: chartQueryKeys.history({ kind: "metrics", categoryId, metricKeys }),
    queryFn: ({ signal }) => fetchHistoryBatched("metrics", { categoryId, keys: metricKeys, signal }),
  });

  // Ratio charts (first 4: MC/Fees, MC/TVL, MC/DexVol, Fees/Txns)
  const ratioKeys = [
    MetricKeys.Ratio_MarketCapToAnnualisedFees,
    MetricKeys.Ratio_MarketCapToTvl,
    MetricKeys.Ratio_MarketCapToDexTradingVolume,
    MetricKeys.Ratio_AnnualisedFeesToTransactions,
  ];

  await queryClient.prefetchQuery({
    queryKey: chartQueryKeys.history({ kind: "ratios", categoryId, ratioKeys }),
    queryFn: ({ signal }) => fetchHistoryBatched("ratios", { categoryId, keys: ratioKeys, signal }),
  });
}

